import { useEffect } from 'react';

/**
 * Custom hook to manage document head (title, meta tags) for SEO.
 * Works with React 19 without needing react-helmet-async.
 * 
 * @param {Object} options
 * @param {string} options.title - Page title
 * @param {string} options.description - Meta description
 * @param {string} [options.canonical] - Canonical URL
 * @param {string} [options.ogImage] - Open Graph image URL
 * @param {string} [options.ogType] - Open Graph type (default: 'website')
 */
const useDocumentHead = ({ title, description, canonical, ogImage, ogType = 'website' }) => {
    useEffect(() => {
        // Set document title
        const fullTitle = title.includes('Tesseractive') ? title : `${title} | Tesseractive Inc.`;
        document.title = fullTitle;

        // Helper to set or create a meta tag
        const setMeta = (attr, attrValue, content) => {
            let element = document.querySelector(`meta[${attr}="${attrValue}"]`);
            if (!element) {
                element = document.createElement('meta');
                element.setAttribute(attr, attrValue);
                document.head.appendChild(element);
            }
            element.setAttribute('content', content);
        };

        // Standard meta tags
        setMeta('name', 'description', description);

        // Open Graph tags
        setMeta('property', 'og:title', fullTitle);
        setMeta('property', 'og:description', description);
        setMeta('property', 'og:type', ogType);
        if (ogImage) {
            setMeta('property', 'og:image', ogImage);
        }
        if (canonical) {
            setMeta('property', 'og:url', canonical);
        }

        // Twitter Card tags
        setMeta('name', 'twitter:card', 'summary_large_image');
        setMeta('name', 'twitter:title', fullTitle);
        setMeta('name', 'twitter:description', description);
        if (ogImage) {
            setMeta('name', 'twitter:image', ogImage);
        }

        // Canonical link
        if (canonical) {
            let link = document.querySelector('link[rel="canonical"]');
            if (!link) {
                link = document.createElement('link');
                link.setAttribute('rel', 'canonical');
                document.head.appendChild(link);
            }
            link.setAttribute('href', canonical);
        }

        // Cleanup: reset title on unmount
        return () => {
            document.title = 'Tesseractive Inc. | AI & Data Science Consulting';
        };
    }, [title, description, canonical, ogImage, ogType]);
};

export default useDocumentHead;
